import AssignedElements from '@app/components/LoggedIn/Menu/AssignedElements';
import menuTypeAssignedCategoriesFactory from '@app/helpers/factories/menu/cardFactories/menuTypes/menuTypeAssignedCategoriesFactory';
import useRefreshMenuWeb from '@app/hooks/useRefreshMenuWeb';
import { actionCreatorsMenuWeb } from '@app/state';
import {
  makeSelectItemData,
  selectCategories,
} from '@app/state/menu/menuSelectors';
import { bindActionCreators } from '@reduxjs/toolkit';
import { BreadCrumbAction, SectionId } from '@westondev/tableturn-core';
import { useEffect, useMemo, useState } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ICategoryCountMap } from '../types';
import useNavigateWithOrg from '@app/hooks/useNavigateWithOrg';
import {
  getPathWithOrgData,
  openNewTabWithOrgData,
} from '@app/helpers/navigation';
import { getSelectionModalStatusFilterValue } from '@app/helpers/menu/modals/itemsSelectionModal';
import FilterGenericSelectionModal from '@app/components/common/SelectionModal/FilterGenericSelectionModal';

interface IAssignedCategoriesSection extends WithTranslation {
  categoryCount: ICategoryCountMap;
}

const SECTION_ID = SectionId.ASSOCIATION;

const getMemoizedItemData = makeSelectItemData('menuTypes', SECTION_ID);

const AssignedCategoriesSection = ({
  categoryCount,
  t,
}: IAssignedCategoriesSection) => {
  const { checkForChangesAndNavigateWeb, addBreadcrumbLocationWeb } =
    bindActionCreators(actionCreatorsMenuWeb, useDispatch());

  const [openAddExistingItem, setOpenAddExistingItem] = useState(false);
  const [statusFilter, setStatusFilter] = useState([1]);
  const [filteredList, setFilteredList] = useState<
    typeof associatedCategoriesCardList
  >([]);

  const categories = useSelector(selectCategories);
  const { itemData } = useSelector(getMemoizedItemData);

  const onRefresh = useRefreshMenuWeb();
  const navigate = useNavigateWithOrg();

  const associatedCategoriesIds = itemData?.categoryIds;

  const associatedCategoriesCardList = useMemo(() => {
    return menuTypeAssignedCategoriesFactory(
      associatedCategoriesIds || [],
      categories,
      categoryCount,
      (id: number, text: string, openInNewTab?: boolean) => {
        const pathURL = `/menu/categories/${id}`;
        if (openInNewTab) return openNewTabWithOrgData(pathURL);

        const _navigate = () => navigate(pathURL);
        checkForChangesAndNavigateWeb(
          () =>
            addBreadcrumbLocationWeb({
              action: BreadCrumbAction.NAV,
              text,
              onPress: _navigate,
              pathURL,
            }),
          onRefresh,
        );
      },
      id => getPathWithOrgData(`/menu/categories/${id}`),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, associatedCategoriesIds, categoryCount, t]);

  const closeModal = () => {
    setOpenAddExistingItem(false);
  };

  useEffect(() => {
    if (!associatedCategoriesCardList) {
      return;
    }
    const list = statusFilter.includes(0)
      ? associatedCategoriesCardList
      : [...associatedCategoriesCardList].filter(element => {
          const isStatusPassed =
            statusFilter.includes(0) ||
            (statusFilter.includes(1) && element.isActive) ||
            (statusFilter.includes(2) && !element.isActive);

          return isStatusPassed;
        });
    setFilteredList(list);
  }, [associatedCategoriesCardList, statusFilter]);

  const STATUS_FILTER_ID = 1;

  const statusOptions = [
    {
      label: t('commonTexts.all'),
      value: 0,
    },
    {
      label: t('commonTexts.active'),
      value: 1,
    },
    {
      label: t('commonTexts.inactive'),
      value: 2,
    },
  ];

  const onFilter = (option: number) => {
    setStatusFilter(getSelectionModalStatusFilterValue(option, statusFilter));
  };

  const onFilterClear = () => {
    setStatusFilter([0]);
  };

  const statusFilterNode = useMemo(
    () => (
      <FilterGenericSelectionModal<number>
        options={[
          {
            id: STATUS_FILTER_ID,
            label: t('commonTexts.status'),
            options: statusOptions,
          },
        ]}
        isActive={!statusFilter.includes(0)}
        currentValue={[{ id: STATUS_FILTER_ID, values: statusFilter }]}
        onFilter={onFilter as (options: unknown) => void}
        onClear={onFilterClear}
        triggerButtonProps={{
          csx: { minWidth: '50px', width: '50px' },
        }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusFilter],
  );

  return (
    <AssignedElements
      noElementsMessage={t(
        'menuScreen.menuTypeDetails.itemAssociations.emptyCardsMessage',
      )}
      active={openAddExistingItem}
      onModalClose={closeModal}
      elements={filteredList}
      subCardProps={{
        title: t('menuScreen.menuTypeDetails.itemAssociations.subtitle'),
        subtitle: t('menuScreen.menuTypeDetails.itemAssociations.label'),
        extraOptions: statusFilterNode,
      }}
    />
  );
};

export default AssignedCategoriesSection;
